export interface LaneRect {
  /** Pixel offset of the lane's top edge, measured from the pane top. */
  top: number
  /** Pixel height of the lane. */
  height: number
}

/**
 * Partition a pane of `paneHeight` px into `count` equal stacked lanes and
 * return the rect for lane `index` (0-based). Shared by the indicator legend
 * (CSS `top`) and the boolean lane-strip draw (canvas `y`) so the two never
 * drift. `count <= 0` collapses to one full-height lane.
 */
export function laneBand(index: number, count: number, paneHeight: number): LaneRect {
  const h = count > 0 ? paneHeight / count : paneHeight
  return { top: index * h, height: h }
}

/**
 * Variable-height lane layout for a strip where hidden lanes collapse to a fixed
 * `collapsedPx` (their legend row) and visible lanes share the remaining height
 * equally. Returns a rect per lane, in order. Deterministic in
 * (`visible`, `paneHeight`, `collapsedPx`), so the strip draw and the legend can
 * both call it and never drift — provided the pane is sized to the matching
 * total (visibleCount·fullLane + hiddenCount·collapsedPx). With all lanes visible
 * this is identical to stacking `laneBand`s.
 */
export function laneBandsVariable(
  visible: boolean[],
  paneHeight: number,
  collapsedPx: number,
): LaneRect[] {
  const n = visible.length
  if (n === 0) return []
  const hiddenCount = visible.reduce((acc, v) => acc + (v ? 0 : 1), 0)
  const visibleCount = n - hiddenCount
  const remaining = paneHeight - hiddenCount * collapsedPx
  const fullLane = visibleCount > 0 ? Math.max(collapsedPx, remaining / visibleCount) : collapsedPx
  const rects: LaneRect[] = []
  let top = 0
  for (const v of visible) {
    const height = v ? fullLane : collapsedPx
    rects.push({ top, height })
    top += height
  }
  return rects
}
